"use client";

import { Crown } from "lucide-react";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import type { TournamentTeamWithDraftLink } from "@/entities/match/lib/match-team-link";
import { rosterHref } from "@/entities/match/lib/match-team-link";
import { cn } from "@/shared/lib/utils";
import { RoleIcon, normalizeLolRole } from "@/shared/ui/data-display/role-icon";
import { SafeAvatar } from "@/shared/ui/data-display/safe-avatar";

/** Порядок линий, привычный по клиенту игры; всё нераспознанное — в конец. */
const ROLE_ORDER = ["top", "jungle", "mid", "adc", "support"] as const;

function roleRank(role: string | null | undefined): number {
  const key = normalizeLolRole(role);
  if (!key) return ROLE_ORDER.length;
  const idx = ROLE_ORDER.indexOf(key as (typeof ROLE_ORDER)[number]);
  return idx === -1 ? ROLE_ORDER.length : idx;
}

export interface TeamLineupProps {
  /** Название из сетки — оно есть всегда, даже когда состав не подтянулся. */
  fallbackName: string;
  team: TournamentTeamWithDraftLink | null;
  /** Правая команда зеркалится, чтобы счёт читался по центру. */
  mirror?: boolean;
  className?: string;
}

/**
 * Состав одной команды матча: логотип, кликабельное название и пять строк
 * «роль — ник — Riot ID». Ради этого блока страницу и открывают, поэтому он
 * не прячется ни за какими вкладками и не зависит от десктоп-приложения.
 */
export function TeamLineup({
  fallbackName,
  team,
  mirror = false,
  className,
}: TeamLineupProps) {
  const tUi = useTranslations("Ui");
  const href = rosterHref(team);
  const name = team?.name || fallbackName;
  const members = [...(team?.members ?? [])].sort(
    (a, b) => roleRank(a.role) - roleRank(b.role),
  );

  const heading = (
    <span className="truncate text-base font-semibold text-foreground">
      {name}
    </span>
  );

  return (
    <div className={cn("min-w-0", className)}>
      <div
        className={cn(
          "flex min-w-0 items-center gap-2.5",
          mirror && "sm:flex-row-reverse sm:text-right",
        )}
      >
        <SafeAvatar
          src={team?.icon ?? team?.logoUrl}
          alt={name}
          size="lg"
          className="shrink-0 rounded-md"
        />
        <div className="min-w-0">
          {href ? (
            <Link
              href={href}
              className="block min-w-0 rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {heading}
            </Link>
          ) : (
            // Команда не сопоставлена с ростером — оставляем текстом, чтобы
            // клик не уводил в 404.
            heading
          )}
          {team?.tag ? (
            <span className="block truncate text-xs text-muted-foreground">
              {team.tag}
            </span>
          ) : null}
        </div>
      </div>

      {members.length > 0 && (
        <ul className="mt-3 space-y-0.5">
          {members.map((m) => {
            const isCaptain = Boolean(m.userId) && m.userId === team?.captainId;
            const riotId =
              m.riotAccountName && m.riotAccountTag
                ? `${m.riotAccountName}#${m.riotAccountTag}`
                : m.riotAccountName || null;

            return (
              <li
                key={m.userId ?? m.displayName}
                className={cn(
                  "flex min-w-0 items-center gap-2 py-1 text-sm",
                  mirror && "sm:flex-row-reverse sm:text-right",
                )}
              >
                <span className="flex w-4 shrink-0 justify-center">
                  <RoleIcon role={m.role} size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "flex min-w-0 items-center gap-1.5",
                      mirror && "sm:flex-row-reverse",
                    )}
                  >
                    <span className="truncate font-medium text-foreground">
                      {m.displayName}
                    </span>
                    {isCaptain && (
                      <Crown
                        className="size-3.5 shrink-0 text-muted-foreground"
                        aria-label={tUi("teamLineup.kapitan")}
                      />
                    )}
                  </span>
                  {riotId && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {riotId}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
